import { useCallback, useEffect, useRef, useState } from 'react';
import i18n from 'i18n/config';

const StreamHealth = {
  LOADING: 'loading',
  HEALTHY: 'healthy',
  INACTIVE: 'inactive',
  NO_STREAM: 'no-stream',
  ERROR: 'error'
};

export const StreamHealthStatus = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error'
};

const getStatusByHealth = () => ({
  [StreamHealth.LOADING]: {
    message: i18n.t('troubleshootingPanel.initializingCameraAndMic'),
    status: StreamHealthStatus.WARNING
  },
  [StreamHealth.INACTIVE]: {
    message: i18n.t('troubleshootingPanel.cameraOrMicNotResponding'),
    status: StreamHealthStatus.ERROR
  },
  [StreamHealth.NO_STREAM]: {
    message: i18n.t('troubleshootingPanel.noCameraOrMic'),
    status: StreamHealthStatus.ERROR
  },
  [StreamHealth.ERROR]: {
    message: i18n.t('troubleshootingPanel.failedToConnect'),
    status: StreamHealthStatus.ERROR
  },
  [StreamHealth.HEALTHY]: {
    message: i18n.t('videoQuestion.cameraAndMicReady'),
    status: StreamHealthStatus.SUCCESS
  }
});

const HEALTH_CHECK_INTERVAL = 3000;

export const useStreamHealth = ({
  stream,
  initializeMedia,
  loading,
  error
}) => {
  const isInitialLoading = useRef(true);
  const healthCheckRef = useRef(null);
  const handleRefreshStreamRef = useRef(null);
  const STATUS_BY_HEALTH = getStatusByHealth();
  const [statusMessage, setStatusMessage] = useState(
    STATUS_BY_HEALTH[StreamHealth.LOADING]
  );

  const validateStreamHealth = useCallback(() => {
    const STATUS_BY_HEALTH = getStatusByHealth();
    if (loading && isInitialLoading.current) {
      setStatusMessage(STATUS_BY_HEALTH[StreamHealth.LOADING]);
      return true;
    } else {
      isInitialLoading.current = false;
    }

    if (loading) {
      return true;
    }

    if (error) {
      setStatusMessage({
        message: error,
        status: StreamHealth.ERROR
      });
      return false;
    }

    if (!stream) {
      setStatusMessage(STATUS_BY_HEALTH[StreamHealth.NO_STREAM]);
      return false;
    }

    const tracks = stream.getTracks();
    const hasActiveTracks =
      tracks.length > 0 && tracks.every(track => track.readyState === 'live');

    if (hasActiveTracks) {
      setStatusMessage(STATUS_BY_HEALTH[StreamHealth.HEALTHY]);
      return true;
    } else {
      setStatusMessage(STATUS_BY_HEALTH[StreamHealth.INACTIVE]);
      return false;
    }
  }, [stream, loading, error]);

  const handleRefreshStream = useCallback(async () => {
    if (validateStreamHealth()) return;

    try {
      await initializeMedia();
    } catch (error) {
      console.error('Failed to refresh stream:', error);
      setStatusMessage({
        message: error,
        status: StreamHealth.ERROR
      });
    }
  }, [validateStreamHealth, initializeMedia]);

  // Keep the ref updated with the latest function
  handleRefreshStreamRef.current = handleRefreshStream;

  // Stream health monitoring and refresh stream if needed
  useEffect(() => {
    // Set up periodic health checks
    healthCheckRef.current = setInterval(
      () => handleRefreshStreamRef.current(),
      HEALTH_CHECK_INTERVAL
    );

    return () => {
      if (healthCheckRef.current) {
        clearInterval(healthCheckRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isInitialLoading.current) {
      validateStreamHealth();
    }
  }, [loading, validateStreamHealth]);

  // Monitor window focus/blur events and refresh stream if needed
  useEffect(() => {
    const handleWindowFocus = () => {
      // Small delay to allow browser to stabilize
      setTimeout(() => {
        handleRefreshStream();
      }, 500);
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [stream, validateStreamHealth, handleRefreshStream]);

  const isNotAbleToRecord = statusMessage.status !== StreamHealthStatus.SUCCESS;

  return {
    statusMessage,
    isNotAbleToRecord
  };
};
